import { useState, useCallback, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useNavigate } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { Edit, Search } from "lucide-react";
import Layout from "../Layout";
import {
  Table as UiTable,
  TableBody as UiTableBody,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
  TableHeader as UiTableHeader,
  TableRow as UiTableRow,
} from "../ui/table";
import navigationRoutes from "../../utils/navigationRoutes";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { SchemaService } from "@/services/APIs";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { formatDate } from "../../utils/dateFormatter";
import { useQuery } from "@tanstack/react-query";

const PP_MyProperty: React.FC = () => {
  const navigate = useNavigate();

  const schemaName = "Property Master";

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>("");

  const myPropertySchemaName = "My Property";

  // Fetch My Property schema columns
  const { data: myPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", myPropertySchemaName],
    queryFn: async () => {
      try {
        const result =
          await SchemaService.getColumnGridData(myPropertySchemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!myPropertySchemaName,
  });

  // Fetch all My Property data to check file uploads
  const { data: allMyPropertyData } = useQuery({
    queryKey: ["my-property-all", myPropertySchemaName, myPropertyColumnData],
    queryFn: async () => {
      if (!myPropertyColumnData?.grid) {
        return { results: [], count: 0 };
      }

      const allColumns = myPropertyColumnData.grid.map(
        (field: any) => field.field_name
      );

      // Add required fields
      const requiredFields = ["id", "propertymasterid", "fileupload"];
      requiredFields.forEach(field => {
        if (
          !allColumns
            .map((col: string) => col.toLowerCase())
            .includes(field.toLowerCase())
        ) {
          allColumns.push(field);
        }
      });

      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          myPropertySchemaName,
          1,
          10000,
          allColumns,
          undefined
        );

        if (response === undefined || response === null) {
          return { results: [], count: 0 };
        }

        if (Array.isArray(response)) {
          return { results: response, count: response.length };
        }
        return response ?? { results: [], count: 0 };
      } catch (error) {
        console.error("Error fetching My Property data:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!myPropertySchemaName && !!myPropertyColumnData,
  });

  // Create a map of property IDs to their file upload status
  const propertyFileUploadStatus = useMemo(() => {
    const statusMap = new Map<string, boolean>();

    let myPropertyArray: any[] = [];
    if (Array.isArray(allMyPropertyData)) {
      myPropertyArray = allMyPropertyData;
    } else if (allMyPropertyData?.results) {
      myPropertyArray = allMyPropertyData.results;
    } else if (allMyPropertyData?.data) {
      myPropertyArray = allMyPropertyData.data;
    }

    myPropertyArray.forEach((record: any) => {
      // Normalize keys to lowercase
      const normalized: any = {};
      Object.keys(record).forEach(key => {
        normalized[key.toLowerCase()] = record[key];
      });

      // Get propertymasterid
      const propertyId =
        normalized["propertymasterid"] ??
        normalized["propertymaster_id"] ??
        record["propertymasterid"] ??
        record["propertymaster_id"] ??
        null;

      if (!propertyId) {
        return;
      }

      // Helper to check if file upload field has data
      const hasFileData = (fieldName: string): boolean => {
        const value =
          normalized[fieldName.toLowerCase()] ??
          record[fieldName] ??
          record[fieldName.toUpperCase()];

        if (!value) {
          return false;
        }

        // Handle string (could be JSON)
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
              return (
                parsed.length > 0 &&
                parsed.some(
                  (item: any) =>
                    item !== null && item !== undefined && item !== ""
                )
              );
            }
            return parsed !== null && parsed !== undefined && parsed !== "";
          } catch {
            return value.trim() !== "";
          }
        }

        // Handle arrays
        if (Array.isArray(value)) {
          return (
            value.length > 0 &&
            value.some(
              (item: any) => item !== null && item !== undefined && item !== ""
            )
          );
        }

        // Handle objects
        if (typeof value === "object" && value !== null) {
          return true;
        }

        return false;
      };

      // Check if the single file upload field has at least one file
      const hasAnyFile = hasFileData("fileupload");

      statusMap.set(String(propertyId), hasAnyFile);
    });

    return statusMap;
  }, [allMyPropertyData]);

  // const handleDelete = useCallback(async (id: string) => {
  //   try {
  //     await DynamicModuleService.deleteItem(schemaName, id);
  //     notify.success("Property deleted successfully!");
  //     queryClient.invalidateQueries({
  //       queryKey: ["moduleData"],
  //       exact: false
  //     });
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     notify.error("Failed to delete property");
  //   }
  // }, [schemaName, queryClient]);

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.EDIT_MY_PROPERTY.replace(":id", id));
  };

  const handleExportToExcel = useCallback(async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      toast.error("Please select at least one property to export");
      return;
    }

    try {
      toast.loading("Exporting properties to Excel...", { id: "export-excel" });

      const myPropertySchemaName = "My Property";
      const maxBidSchemaName = "Max Bid";
      const rehabEstimateSchemaName = "Rehab Estimate";

      // Get schema columns
      const [myPropertyColumnData, maxBidColumnData, rehabEstimateColumnData] =
        await Promise.all([
          SchemaService.getColumnGridData(myPropertySchemaName),
          SchemaService.getColumnGridData(maxBidSchemaName),
          SchemaService.getColumnGridData(rehabEstimateSchemaName),
        ]);

      // Build column lists
      const allMyPropertyColumns =
        myPropertyColumnData?.grid?.map((field: any) => field.field_name) ?? [];
      const requiredMyPropertyFields = [
        "id",
        "propertymasterid",
        "fileupload",
        "remarks",
        "notes",
      ];
      requiredMyPropertyFields.forEach(field => {
        if (
          !allMyPropertyColumns
            .map((col: string) => col.toLowerCase())
            .includes(field.toLowerCase())
        ) {
          allMyPropertyColumns.push(field);
        }
      });

      const allMaxBidColumns =
        maxBidColumnData?.grid?.map((field: any) => field.field_name) ?? [];
      if (
        !allMaxBidColumns.map((col: string) => col.toLowerCase()).includes("id")
      ) {
        allMaxBidColumns.push("id");
      }

      const allRehabColumns =
        rehabEstimateColumnData?.grid?.map((field: any) => field.field_name) ??
        [];
      if (
        !allRehabColumns.map((col: string) => col.toLowerCase()).includes("id")
      ) {
        allRehabColumns.push("id");
      }

      // Fetch Property Master data
      const propertiesResponses = await Promise.all(
        selectedRowKeys.map(id =>
          DynamicModuleService.getItemById(schemaName, String(id))
        )
      );

      const propertiesData = propertiesResponses.map((response: any) => {
        return response?.data ?? response ?? {};
      });

      // Fetch My Property, Max Bid, and Rehab Estimate data for each property
      const myPropertyDataMap = new Map<string, any>();
      const maxBidDataMap = new Map<string, any>();
      const rehabEstimateDataMap = new Map<string, any>();

      await Promise.all(
        selectedRowKeys.map(async propertyId => {
          try {
            // Fetch My Property data
            const myPropertyResponse =
              await DynamicModuleService.getGridCoByConfig(
                myPropertySchemaName,
                1,
                1000,
                allMyPropertyColumns,
                {
                  propertymasterid: String(propertyId),
                }
              );

            const myPropertyData =
              myPropertyResponse?.results?.[0] ??
              myPropertyResponse?.data?.[0] ??
              myPropertyResponse?.[0] ??
              null;
            if (myPropertyData) {
              myPropertyDataMap.set(String(propertyId), myPropertyData);
            }

            // Fetch Max Bid data
            const maxBidResponse = await DynamicModuleService.getGridCoByConfig(
              maxBidSchemaName,
              1,
              1000,
              allMaxBidColumns,
              {
                propertymasterid: String(propertyId),
              }
            );

            const maxBidData =
              maxBidResponse?.results?.[0] ??
              maxBidResponse?.data?.[0] ??
              maxBidResponse?.[0] ??
              null;
            if (maxBidData) {
              maxBidDataMap.set(String(propertyId), maxBidData);
            }

            // Fetch Rehab Estimate data
            const rehabResponse = await DynamicModuleService.getGridCoByConfig(
              rehabEstimateSchemaName,
              1,
              1000,
              allRehabColumns,
              {
                propertymasterid: String(propertyId),
              }
            );

            const rehabData =
              rehabResponse?.results?.[0] ??
              rehabResponse?.data?.[0] ??
              rehabResponse?.[0] ??
              null;
            if (rehabData) {
              rehabEstimateDataMap.set(String(propertyId), rehabData);
            }
          } catch (error) {
            console.error(
              `Error fetching related data for property ${propertyId}:`,
              error
            );
          }
        })
      );

      // Helper function to get field value (case-insensitive)
      const getFieldValue = (data: any, key: string): any => {
        if (!data) {
          return null;
        }
        const fieldKey = Object.keys(data).find(
          k => k.toLowerCase() === key.toLowerCase()
        );
        return fieldKey ? data[fieldKey] : null;
      };

      // (file name extraction removed - not required for export payload)

      // Prepare data for Excel
      const excelData = propertiesData.map((property: any) => {
        const propertyId = property.id ?? property.ID ?? "";
        const myPropertyData = myPropertyDataMap.get(String(propertyId)) ?? {};
        const maxBidData = maxBidDataMap.get(String(propertyId)) ?? {};
        const rehabData = rehabEstimateDataMap.get(String(propertyId)) ?? {};

        // Get values from My Property (preferred) or Property Master (fallback)
        const propertyname =
          getFieldValue(myPropertyData, "propertyname") ??
          getFieldValue(property, "propertyname") ??
          "";
        const yearbuilt =
          getFieldValue(myPropertyData, "yearbuilt") ??
          getFieldValue(property, "yearbuilt") ??
          "";
        const persqftprice =
          getFieldValue(myPropertyData, "persqftprice") ??
          getFieldValue(property, "persqftprice") ??
          "";
        const bed =
          getFieldValue(myPropertyData, "bed") ??
          getFieldValue(property, "bed") ??
          "";
        const bath =
          getFieldValue(myPropertyData, "bath") ??
          getFieldValue(property, "bath") ??
          "";
        const sqfootage =
          getFieldValue(myPropertyData, "sqfootage") ??
          getFieldValue(property, "sqfootage") ??
          "";
        const address =
          getFieldValue(myPropertyData, "address") ??
          getFieldValue(property, "address") ??
          "";
        const rougharv =
          getFieldValue(myPropertyData, "rougharv") ??
          getFieldValue(property, "rougharv") ??
          "";
        const finalarv =
          getFieldValue(myPropertyData, "finalarv") ??
          getFieldValue(property, "finalarv") ??
          "";
        const notes = getFieldValue(property, "notes") ?? "";
        const maxbid = getFieldValue(maxBidData, "maxbid") ?? "";
        const rehabestimate =
          getFieldValue(myPropertyData, "rehabestimate") ??
          getFieldValue(rehabData, "rehabestimate") ??
          "";
        const rehabtype = getFieldValue(rehabData, "rehabtype") ?? "";
        // rehabvalue intentionally unused in export

        const remarks = getFieldValue(myPropertyData, "remarks") ?? "";
        const notesManual = getFieldValue(myPropertyData, "notes") ?? "";

        return {
          "Property Name": propertyname,
          "Year Built": yearbuilt,
          "Per Sqft Price": persqftprice,
          Bed: bed,
          Bath: bath,
          "Sq Footage": sqfootage,
          Address: address,
          "Rough ARV": rougharv,
          "Final ARV": finalarv,
          Notes: notes ?? "--",
          "Max Bid": maxbid,
          "Rehab Estimate": rehabestimate,
          "Rehab type": rehabtype,
          Remarks: remarks,
          "Notes (Manual)": notesManual,
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "My Properties");

      // Generate Excel file and download
      const fileName = `My_Properties_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Successfully exported ${selectedRowKeys.length} property(ies) to Excel`,
        { id: "export-excel" }
      );
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error?.response?.data?.message ??
          "Failed to export properties to Excel",
        { id: "export-excel" }
      );
    }
  }, [selectedRowKeys, schemaName]);

  const columns: ProColumns<any>[] = [
    {
      title: "Date",
      dataIndex: "date",
      render: (_, record) => formatDate(record.date),
    },
    {
      title: "Property Name",
      dataIndex: "propertyname",
    },
    {
      title: "Lien Amount",
      dataIndex: "lienamount",
    },
    {
      title: "Bed",
      dataIndex: "bed",
    },
    {
      title: "Bath",
      dataIndex: "bath",
    },
    {
      title: "Sq. Footage",
      dataIndex: "sqfootage",
    },
    {
      title: "Status",
      width: 120,
      render: (_, record) => {
        const propertyId =
          record.propertymasterid ??
          record.propertymaster_id ??
          record.id ??
          record.ID ??
          record.Id ??
          record._id;

        const propertyIdString = propertyId ? String(propertyId) : null;
        const hasFiles = propertyIdString
          ? (propertyFileUploadStatus.get(propertyIdString) ?? false)
          : false;
        const status = hasFiles ? "Completed" : "Pending";

        return (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              hasFiles
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      hideInTable: true,
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <Button
          key="edit"
          variant="ghost"
          size="icon"
          onClick={() => handleEdit(record.id)}
        >
          <Edit size={16} />
        </Button>,
        // <Popconfirm
        //   key="delete"
        //   title="Are you sure you want to delete this property?"
        //   onConfirm={() => handleDelete(record.id)}
        //   okText="Yes"
        //   cancelText="No"
        // >
        //   <Button variant="ghost" size="icon">
        //     <Trash size={16} />
        //   </Button>
        // </Popconfirm>,
      ],
    },
  ];

  console.log("Columns defined:", columns);
  console.log(
    "DataIndex values being sent to API:",
    columns.filter(col => col.dataIndex).map(col => col.dataIndex)
  );

  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
  }, []);

  // Fetch module data for table
  const { data: moduleData, isLoading: moduleLoading } = useQuery({
    queryKey: ["moduleData", schemaName, activeSearchTerm],
    queryFn: async () => {
      try {
        const columnsToFetch = columns
          .filter(col => (col as any).dataIndex)
          .map(col => (col as any).dataIndex as string);
        if (!columnsToFetch.map(c => c.toLowerCase()).includes("id")) {
          columnsToFetch.push("id");
        }
        const resp = await DynamicModuleService.getGridCoByConfig(
          schemaName,
          1,
          1000,
          columnsToFetch,
          {}
        );
        return resp ?? { results: [], count: 0 };
      } catch (err) {
        console.error("Error fetching module data:", err);
        return { results: [], count: 0 };
      }
    },
    enabled: !!schemaName,
  });

  const rawTableData = useMemo(() => {
    if (!moduleData) {
      return [];
    }
    return Array.isArray(moduleData) ? moduleData : (moduleData.results ?? []);
  }, [moduleData]);

  const filteredTableData = useMemo(() => {
    if (!activeSearchTerm) {
      return rawTableData;
    }
    const term = activeSearchTerm.toLowerCase();
    return rawTableData.filter((item: any) => {
      const propertyName = item.propertyname ?? item.propertyName ?? "";
      return String(propertyName).toLowerCase().includes(term);
    });
  }, [rawTableData, activeSearchTerm]);

  const getIdFromRecord = (rec: any) =>
    rec.id ??
    rec.ID ??
    rec.Id ??
    rec._id ??
    (Object.keys(rec ?? {}).find(k => k.toLowerCase() === "id")
      ? rec[Object.keys(rec).find(k => k.toLowerCase() === "id") as string]
      : undefined);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredTableData
        .map((r: any) => String(getIdFromRecord(r)))
        .filter(Boolean);
      setSelectedRowKeys(allIds);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const idStr = String(id);
    if (checked) {
      setSelectedRowKeys(prev => Array.from(new Set([...(prev ?? []), idStr])));
    } else {
      setSelectedRowKeys(prev => (prev ?? []).filter(k => String(k) !== idStr));
    }
  };

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            My Properties
          </h1>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-[#3d3d7a] bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={handleExportToExcel}
              disabled={!selectedRowKeys || selectedRowKeys.length === 0}
            >
              Export Excel
            </Button>
            {/* <Button
              variant="outline"
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90 border-[#3d3d7a]"
            >
              Upload
            </Button>
            <Button
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={() => navigate(navigationRoutes.CREATE_PROPERTY)}
            >
              + Add Property
            </Button> */}
          </div>
        </div>

        {/* Search Box */}
        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by property name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                type="button"
              >
                ×
              </button>
            )}
          </div>
          <Button
            type="button"
            onClick={handleSearch}
            className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
            disabled={!searchTerm.trim()}
          >
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
          {activeSearchTerm && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <UiTable>
              <UiTableHeader>
                <UiTableRow className="bg-muted/50">
                  <UiTableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={
                        filteredTableData.length > 0 &&
                        selectedRowKeys.length > 0 &&
                        selectedRowKeys.length ===
                          filteredTableData
                            .map((r: any) => String(getIdFromRecord(r)))
                            .filter(Boolean).length
                      }
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                    />
                  </UiTableHead>
                  <UiTableHead>NO.</UiTableHead>
                  <UiTableHead>Date</UiTableHead>
                  <UiTableHead>Property Name</UiTableHead>
                  <UiTableHead>Lien Amount</UiTableHead>
                  <UiTableHead>Bed</UiTableHead>
                  <UiTableHead>Bath</UiTableHead>
                  <UiTableHead>Sq. Footage</UiTableHead>
                  <UiTableHead>Status</UiTableHead>
                  <UiTableHead>ACTION</UiTableHead>
                </UiTableRow>
              </UiTableHeader>
              <UiTableBody>
                {moduleLoading ? (
                  <UiTableRow>
                    <UiTableCell colSpan={10} className="py-8 text-center">
                      Loading...
                    </UiTableCell>
                  </UiTableRow>
                ) : filteredTableData.length === 0 ? (
                  <UiTableRow>
                    <UiTableCell colSpan={10} className="py-8 text-center">
                      No properties found
                    </UiTableCell>
                  </UiTableRow>
                ) : (
                  filteredTableData.map((record: any, idx: number) => {
                    const id = String(getIdFromRecord(record) ?? idx);
                    const dateVal = formatDate(
                      record.date ?? record.Date ?? record.created_at
                    );
                    const propertyName =
                      record.propertyname ?? record.propertyName ?? "-";
                    const lienAmount =
                      record.lienamount ?? record.lienAmount ?? "-";
                    const bed = record.bed ?? "-";
                    const bath = record.bath ?? "-";
                    const sq = record.sqfootage ?? record.sqFootage ?? "-";

                    const hasFiles =
                      propertyFileUploadStatus.get(String(id)) ?? false;
                    const status = hasFiles ? "Completed" : "Pending";

                    const isSelected = (selectedRowKeys ?? []).some(
                      k => String(k) === id
                    );

                    return (
                      <UiTableRow key={id}>
                        <UiTableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e =>
                              handleSelectRow(id, e.target.checked)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                          />
                        </UiTableCell>
                        <UiTableCell>{idx + 1}</UiTableCell>
                        <UiTableCell>{dateVal}</UiTableCell>
                        <UiTableCell className="font-medium">
                          {propertyName}
                        </UiTableCell>
                        <UiTableCell>{lienAmount}</UiTableCell>
                        <UiTableCell>{bed}</UiTableCell>
                        <UiTableCell>{bath}</UiTableCell>
                        <UiTableCell>{sq}</UiTableCell>
                        <UiTableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {status}
                          </span>
                        </UiTableCell>
                        <UiTableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(id)}
                            >
                              <Edit className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </div>
                        </UiTableCell>
                      </UiTableRow>
                    );
                  })
                )}
              </UiTableBody>
            </UiTable>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PP_MyProperty;
